/**
 * useSidebarDragOver — detects when a dragged task hovers over a sidebar item.
 *
 * Because @dnd-kit uses per-view DndContext (sidebar is outside), we can't
 * use `useDroppable`. Instead, we track pointer position during drag and
 * use `document.elementsFromPoint()` to detect sidebar items annotated with
 * `data-sidebar-drop` attributes.
 *
 * Uses `elementsFromPoint` (plural) to see through the DragOverlay and any
 * other overlapping layers — this returns ALL elements at the coordinates,
 * not just the topmost one.
 *
 * Updates `viewContext.sidebarDropTarget` for visual highlight in Sidebar.
 */

import { useEffect, useRef } from "react"
import { useViewContext } from "@/app/stores/viewContext"
import { isOverSidebarRef, DND_SIDEBAR_EXIT_EVENT } from "./dndSidebarState"
import { canDropOnSidebarTarget } from "./dndConstraints"

/**
 * Find the closest sidebar drop target from a list of elements at a point.
 * Walks each element and its ancestors looking for `[data-sidebar-drop]`.
 */
function findDropTarget(elements: Element[]): string | null {
  for (const el of elements) {
    const dropEl = el.closest("[data-sidebar-drop]")
    if (dropEl) {
      return dropEl.getAttribute("data-sidebar-drop")
    }
  }
  return null
}

/**
 * Check if pointer coordinates are inside the sidebar panel's bounding box.
 * Uses getBoundingClientRect — immune to z-order / pointer-events issues
 * that make elementsFromPoint unreliable during DragOverlay rendering.
 */
function isInsideSidebar(x: number, y: number): boolean {
  const el = document.querySelector("[data-sidebar-panel]")
  if (!el) return false
  const rect = el.getBoundingClientRect()
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
}

export function useSidebarDragOver(): void {
  const isDragging = useViewContext((s) => s.isDragging)
  const setSidebarDropTarget = useViewContext((s) => s.setSidebarDropTarget)
  const currentTargetRef = useRef<string | null>(null)
  const overSidebarRef = useRef(false)

  useEffect(() => {
    if (!isDragging) {
      // Clear target when not dragging
      if (currentTargetRef.current !== null) {
        currentTargetRef.current = null
        setSidebarDropTarget(null)
      }
      if (overSidebarRef.current) {
        overSidebarRef.current = false
      }
      // Always clean up body class + shared ref on drag end
      document.body.classList.remove("dnd-over-sidebar")
      document.body.classList.remove("dnd-expanding")
      isOverSidebarRef.current = false
      return
    }

    const handlePointerMove = (e: PointerEvent | MouseEvent | TouchEvent) => {
      let x: number
      let y: number

      if ("touches" in e && e.touches.length > 0) {
        x = e.touches[0]!.clientX
        y = e.touches[0]!.clientY
      } else if ("clientX" in e) {
        x = e.clientX
        y = e.clientY
      } else {
        return
      }

      // elementsFromPoint returns ALL elements at the coordinates,
      // penetrating through pointer-events:none layers (DragOverlay).
      const elements = document.elementsFromPoint(x, y)

      // Detect specific drop target (e.g. "inbox", "today")
      const rawTarget = findDropTarget(elements)

      // §33 Constraint filtering: suppress highlight if drag source can't drop here
      let target = rawTarget
      if (rawTarget) {
        const sourceInfo = useViewContext.getState().dragSourceInfo
        if (sourceInfo && !canDropOnSidebarTarget(sourceInfo, rawTarget)) {
          target = null
        }
      }

      if (target !== currentTargetRef.current) {
        currentTargetRef.current = target
        setSidebarDropTarget(target)
      }

      // Detect if pointer is anywhere inside the sidebar panel.
      // Uses bounding rect instead of elementsFromPoint — DragOverlay's
      // portal interferes with element detection during drag.
      const overPanel = isInsideSidebar(x, y)
      if (overPanel !== overSidebarRef.current) {
        overSidebarRef.current = overPanel

        if (overPanel) {
          // Entering sidebar — cancel any pending expand animation,
          // set body class + shared ref synchronously (before next paint).
          document.body.classList.remove("dnd-expanding")
          isOverSidebarRef.current = true
          document.body.classList.add("dnd-over-sidebar")
        } else {
          // Leaving sidebar — swap collapse → expand animation class.
          // Shared ref stays true (rects are stale until DroppableRectRefresher
          // re-measures after the animation).
          document.body.classList.remove("dnd-over-sidebar")
          document.body.classList.add("dnd-expanding")
          document.dispatchEvent(new Event(DND_SIDEBAR_EXIT_EVENT))
        }
      }
    }

    // Listen on capture phase to intercept before @dnd-kit's handlers
    document.addEventListener("pointermove", handlePointerMove, true)
    document.addEventListener("mousemove", handlePointerMove, true)
    document.addEventListener("touchmove", handlePointerMove, true)

    return () => {
      document.removeEventListener("pointermove", handlePointerMove, true)
      document.removeEventListener("mousemove", handlePointerMove, true)
      document.removeEventListener("touchmove", handlePointerMove, true)
      currentTargetRef.current = null
      overSidebarRef.current = false
      setSidebarDropTarget(null)
      // Clean up body class + shared ref
      document.body.classList.remove("dnd-over-sidebar")
      document.body.classList.remove("dnd-expanding")
      isOverSidebarRef.current = false
    }
  }, [isDragging, setSidebarDropTarget])
}
